import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
} from 'react-native';
import { ADMIN_COLORS, ROWS_PER_PAGE } from '@/constants/admin';

// Component imports
import { RsvpListItem } from './RsvpListItem';

// Types
import { EventRsvpsTabProps, RsvpStats, RsvpQueryParams } from './types';
import { StatsCard } from '@/components/common/admin/mobile/StatsCard';
import { downloadCsvFrom2DArray } from '@/utils/admin/csvExport';

export const EventRsvpsTab: React.FC<EventRsvpsTabProps> = ({ event }) => {
  const [currentPage, setCurrentPage] = useState(0);

  // Filter RSVPs from event data (matching admin-web logic)
  const filteredRsvps = useMemo(() => {
    return event.rsvpList?.filter((resident) => {
      return resident.EventResident?.hasRsvp;
    }) || [];
  }, [event.rsvpList]);

  const totalRsvps = filteredRsvps.length;
  const totalPages = Math.ceil(totalRsvps / ROWS_PER_PAGE);
  
  // Paginate the filtered RSVPs client-side (matching admin-web)
  const rsvpList = useMemo(() => {
    const startIndex = currentPage * ROWS_PER_PAGE;
    const endIndex = startIndex + ROWS_PER_PAGE;
    return filteredRsvps.slice(startIndex, endIndex);
  }, [filteredRsvps, currentPage, ROWS_PER_PAGE]);

  // Calculate stats
  const stats = useMemo((): RsvpStats => ({
    totalRsvps,
    maxAttendees: event.rsvpLimit === 0 ? 'Unlimited' : event.rsvpLimit,
  }), [totalRsvps, event.rsvpLimit]);

  // Export RSVPs (all filtered RSVPs, not just current page)
  const handleExport = useCallback(() => {
    const CSV_COLUMNS = ['Name', 'Email', 'Unit', 'RSVP', 'Checked In'] as const;
    const rows = [
      CSV_COLUMNS as unknown as (string | number | boolean | null)[],
      ...filteredRsvps.map((resident) => {
        const name = `${resident.firstName} ${resident.lastName}`.trim();
        const email = resident.email || '';
        const unit = resident.unitNumberSpace || '';
        const rsvp = resident.EventResident?.hasRsvp ? 'Yes' : 'No';
        const checkedIn = resident.EventResident?.checkedIn ? 'Yes' : 'No';
        return [name, email, unit, rsvp, checkedIn];
      }),
    ];
    const filename = `${event.name}_rsvps`;
    downloadCsvFrom2DArray(rows as any, filename);
  }, [filteredRsvps, event.id]);

  // Event handlers
  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(0);
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages - 1);
  };

  // No loading/error states needed since we're using event data directly

  return (
    <View style={styles.container}>
      {/* RSVP Stats Cards - Top Section */}
      <View style={styles.statsSection}>
        <View style={styles.statsRow}>
          <StatsCard
            title="RSVPs"
            value={stats.totalRsvps}
            color={ADMIN_COLORS.SUCCESS}
            icon="✓"
          />
          <StatsCard
            title="Max Attendees"
            value={stats.maxAttendees}
            color={ADMIN_COLORS.PRIMARY}
            icon="👥"
          />
        </View>
      </View>

      {/* RSVP List Section */}
      <View style={styles.listSection}>
        <View style={styles.listHeader}>
          <Text style={styles.listTitle}>Who has RSVP'd ({totalRsvps})</Text>
          <TouchableOpacity style={styles.exportButton} onPress={handleExport}>
            <Text style={styles.exportButtonText}>Export</Text>
          </TouchableOpacity>
        </View>

        {rsvpList.length === 0 ? (
          <View style={styles.noRsvpsContainer}>
            <Text style={styles.noRsvpsText}>No RSVPs yet</Text>
            <Text style={styles.noRsvpsSubtext}>
              Residents haven't RSVP'd to this event yet
            </Text>
          </View>
        ) : (
          <>
            <FlatList
              data={rsvpList}
              keyExtractor={(item) => item.id.toString()}
              renderItem={({ item }) => <RsvpListItem resident={item} />}
              style={styles.rsvpList}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.listContent}
            />

            {/* Mobile-friendly Pagination */}
            {totalPages > 1 && (
              <View style={styles.paginationContainer}>
                <View style={styles.paginationInfo}>
                  <Text style={styles.paginationText}>
                    Page {currentPage + 1} of {totalPages}
                  </Text>
                </View>
                <View style={styles.paginationControls}>
                  <TouchableOpacity
                    style={[
                      styles.paginationButtonSmall,
                      currentPage === 0 && styles.paginationButtonDisabled,
                    ]}
                    onPress={handleFirstPage}
                    disabled={currentPage === 0}
                    accessibilityLabel="First page"
                    accessibilityHint={currentPage === 0 ? "Already on first page" : "Go to first page of RSVPs"}
                    accessibilityRole="button"
                  >
                    <Text style={styles.paginationButtonText}>First</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.paginationButton,
                      currentPage === 0 && styles.paginationButtonDisabled,
                    ]}
                    onPress={handlePreviousPage}
                    disabled={currentPage === 0}
                    accessibilityLabel="Previous page"
                    accessibilityHint={currentPage === 0 ? "No previous page available" : "Go to previous page of RSVPs"}
                    accessibilityRole="button"
                  >
                    <Text style={styles.paginationButtonText}>Previous</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.paginationButton,
                      currentPage >= totalPages - 1 && styles.paginationButtonDisabled,
                    ]}
                    onPress={handleNextPage}
                    disabled={currentPage >= totalPages - 1}
                    accessibilityLabel="Next page"
                    accessibilityHint={currentPage >= totalPages - 1 ? "No next page available" : "Go to next page of RSVPs"}
                    accessibilityRole="button"
                  >
                    <Text style={styles.paginationButtonText}>Next</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.paginationButtonSmall,
                      currentPage >= totalPages - 1 && styles.paginationButtonDisabled,
                    ]}
                    onPress={handleLastPage}
                    disabled={currentPage >= totalPages - 1}
                    accessibilityLabel="Last page"
                    accessibilityHint={currentPage >= totalPages - 1 ? "Already on last page" : "Go to last page of RSVPs"}
                    accessibilityRole="button"
                  >
                    <Text style={styles.paginationButtonText}>Last</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  // Stats Section - Top
  statsSection: {
    marginBottom: 24,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  // List Section
  listSection: {
    flex: 1,
  },
  listHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  listTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  exportButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  exportButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  rsvpList: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 20,
  },
  // Empty State
  noRsvpsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  noRsvpsText: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  noRsvpsSubtext: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 24,
  },
  // Mobile-friendly Pagination
  paginationContainer: {
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER + '40',
  },
  paginationInfo: {
    alignItems: 'center',
    marginBottom: 12,
  },
  paginationText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  paginationControls: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
  },
  paginationButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
    minWidth: 100,
    alignItems: 'center',
  },
  paginationButtonSmall: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.PRIMARY,
    minWidth: 70,
    alignItems: 'center',
  },
  paginationButtonDisabled: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
  },
  paginationButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  // Loading and Error States
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  errorText: {
    fontSize: 20,
    fontWeight: '600',
    color: ADMIN_COLORS.ERROR,
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
});
